import { computed, onMounted, ref } from 'vue';
import {
  canSaveCommissionPlan,
  formFromPlan,
  moneyCents,
  validateCommissionPlan,
} from './realEstateCommissionPlanForm';
import {
  resizeInstallments,
  syncIntegralInstallment,
} from './realEstateCommissionPlanInstallments';
import { createRealEstateCommissionPlanPersistence } from './realEstateCommissionPlanPersistence';

export function useRealEstateCommissionPlan({ id, notify, t }) {
  const plan = ref(null);
  const form = ref(formFromPlan(null));
  const editing = ref(false);
  const loading = ref(false);
  const saving = ref(false);
  const error = ref('');
  const errors = ref({});

  const grossCents = computed(() => moneyCents(form.value.grossAmount));
  const deductionCents = computed(() => moneyCents(form.value.deduction));
  const netCents = computed(() => grossCents.value - deductionCents.value);
  const installmentTotalCents = computed(() =>
    form.value.installments.reduce(
      (total, installment) => total + moneyCents(installment.amount),
      0
    )
  );
  const installmentsMatch = computed(
    () => installmentTotalCents.value === netCents.value
  );
  const sentCount = computed(
    () => plan.value?.parcelas_empresa?.filter(item => item.enviada).length || 0
  );
  const canSave = computed(() =>
    canSaveCommissionPlan({
      form: form.value,
      installmentsMatch: installmentsMatch.value,
    })
  );

  const update = (field, value) => {
    form.value = { ...form.value, [field]: value };
    delete errors.value[field];
    form.value = syncIntegralInstallment({
      form: form.value,
      netCents: netCents.value,
    });
  };

  const updateInstallment = (index, field, value) => {
    form.value = {
      ...form.value,
      installments: form.value.installments.map(
        (installment, installmentIndex) =>
          installmentIndex === index
            ? { ...installment, [field]: value }
            : installment
      ),
    };
    delete errors.value.installments;
  };

  const setModality = modality => {
    form.value = { ...form.value, modality };
    if (modality === 'integral') {
      form.value = syncIntegralInstallment({
        form: form.value,
        netCents: netCents.value,
      });
    }
  };

  const setInstallmentCount = count => {
    form.value = resizeInstallments({ form: form.value, count });
  };

  const startEditing = () => {
    form.value = formFromPlan(plan.value);
    editing.value = true;
    errors.value = {};
  };

  const cancelEditing = () => {
    form.value = formFromPlan(plan.value);
    editing.value = false;
    errors.value = {};
  };

  const validate = () => {
    const result = validateCommissionPlan({ form: form.value, t });
    errors.value = result.errors;
    return result.valid;
  };

  const persistence = createRealEstateCommissionPlanPersistence({
    editing,
    error,
    form,
    id,
    loading,
    notify,
    plan,
    saving,
    t,
    validate,
  });
  const { load, save } = persistence;

  onMounted(load);

  return {
    canSave,
    cancelEditing,
    deductionCents,
    editing,
    error,
    errors,
    form,
    grossCents,
    installmentTotalCents,
    installmentsMatch,
    load,
    loading,
    netCents,
    plan,
    save,
    saving,
    sentCount,
    setInstallmentCount,
    setModality,
    startEditing,
    update,
    updateInstallment,
  };
}
